/**
 * The client half of the open-redirect check.
 *
 * `mdm.auth.login_state.safe_redirect_path` is the authority — this is
 * the same rule applied before a link is built, so the app never
 * constructs a URL the server will silently drop. Keep the two in step;
 * the tests on either side name each other.
 */

const MAX_LENGTH = 2048;

/**
 * Control characters, and the backslash a browser reads as an authority
 * separator in `/\evil.example`.
 *
 * A loop rather than a regex: the character class for "below 0x20, plus
 * DEL, plus a literal backslash" is the kind of escaping that goes wrong
 * quietly. Written as a class it is easy to produce a *range* from space
 * to backslash instead, which refuses every ordinary path and looks like
 * a routing bug.
 */
function hasRefusedCharacter(value: string): boolean {
  for (const character of value) {
    const code = character.codePointAt(0) ?? 0;
    if (code < 0x20 || code === 0x7f || character === "\\") return true;
  }
  return false;
}

export function safeReturnPath(value: string | null | undefined): string | undefined {
  if (value === null || value === undefined || value === "") return undefined;
  if (value.length > MAX_LENGTH) return undefined;
  if (hasRefusedCharacter(value)) return undefined;
  if (!value.startsWith("/") || value.startsWith("//")) return undefined;
  return value;
}

/** The current location, as a return path. */
export function currentReturnPath(): string {
  return `${window.location.pathname}${window.location.search}`;
}
